import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

import { resetLastTimeStoreUpdated } from 'features/store/hooks/reset-last-time-store-updated';
import { useStoreAvailabilityFns } from 'features/store/hooks/use-store-availability-fns';
import { Alert } from 'react-native';

import { IBaseProps, Nullable } from '@rbi-ctg/frontend';
import { IRestaurant } from '@rbi-ctg/store';
import { toaster } from 'components/toaster/toaster';
import { ServiceMode } from 'generated/graphql-gateway';
import { useRoute } from 'hooks/navigation/use-route';
// TODO: This is the original DeliveryFeeSurchargeDialog, we need to use this once the useDialogModal
// renders ok within a modal screen (after GST-5015 is done)
// import useDialogModal from 'hooks/use-dialog-modal';
import { useSyncStorageNumber } from 'hooks/use-local-storage-state/use-sync-storage-state';
import {
  CustomEventNames,
  EventTypes,
  logRBIEvent,
  updateUniversalAttributes,
} from 'state/amplitude';
import { serializePickupMode, serializeServiceMode } from 'state/amplitude/utils';
import { selectors, useAppSelector } from 'state/global-state';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { updateLaunchDarklyUser } from 'state/launchdarkly/launchdarkly-core';
import { useUIContext } from 'state/ui';
import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';
import noop from 'utils/noop';
import {
  getHeadingForServiceMode,
  getPreferredDriveThruServiceMode,
  getServiceModeDescriptionTextId,
} from 'utils/service-mode';

import { SERVICE_MODE_ORDER, serviceModeImages } from './constants';
import { IServiceModeCtx, ServiceModeOption } from './types';

export const getServiceModeFromLS = (): ServiceMode | null => {
  const preloaded = syncStorage.getObject<{ serviceMode: ServiceMode | null }>(
    SyncStorageKey.SERVICE_MODE
  );
  return preloaded ? preloaded.serviceMode : null;
};

const persistServiceMode = (serviceMode: Nullable<ServiceMode>) => {
  syncStorage.setObject(SyncStorageKey.SERVICE_MODE, { serviceMode });
};

export const ServiceModeContext = createContext<IServiceModeCtx>({
  serviceMode: null,
  selectServiceMode: noop,
  setDeliverySurchargeFee: noop,
  isDelivery: false,
  isTakeout: false,
  isEatIn: false,
  isMobileOrderForDriveThru: false,
  isDriveThru: false,
  getServiceModeOptions: () => [],
});

// Non-reactive: reads live value from MMKV but does NOT re-render
// when serviceMode changes. Use for hooks/components that only need
// the value inside callbacks or effects triggered by other state.
export const useServiceModeContext = () => {
  const ctx = useContext(ServiceModeContext);
  const sm = getServiceModeFromLS();
  return {
    ...ctx,
    serviceMode: sm,
    isDelivery: sm === ServiceMode.DELIVERY,
    isTakeout: sm === ServiceMode.TAKEOUT,
    isEatIn: sm === ServiceMode.EAT_IN,
    isMobileOrderForDriveThru: sm === ServiceMode.MOBILE_ORDER_DRIVE_THRU,
    isDriveThru: sm === ServiceMode.DRIVE_THRU,
  };
};

// Reactive: re-renders the subscriber when serviceMode changes.
// Use ONLY for components that display the service mode in their UI.
const LiveServiceModeContext = createContext<ServiceMode | null>(null);

export const useServiceModeLive = () => useContext(LiveServiceModeContext);

export const ServiceModeProvider = ({ children }: IBaseProps) => {
  const { formatCurrencyForLocale: currencyFormatter } = useUIContext();
  const isPricingOrder = useAppSelector(selectors.ordering.selectIsPricingOrder);
  const mobileOrderDriveThruStores = useFlag(LaunchDarklyFlag.MOBILE_ORDER_DRIVE_THRU_STORES);

  const { isServiceModeAvailable } = useStoreAvailabilityFns();
  const isFastLaneEnabled = useFlag(LaunchDarklyFlag.ENABLE_FAST_LANE_DRIVE_THRU);
  const isMobileOrderForDriveThruLaneEnabled = useFlag(
    LaunchDarklyFlag.ENABLE_MOBILE_ORDER_DRIVE_THRU_LANE
  );

  const { params } = useRoute<{ 'service-mode': string }>();
  const serviceModeEntry = Object.entries(ServiceMode).find(
    ([k]) => k === params['service-mode']
  )?.[1];
  const serviceModeInRoute = serviceModeEntry ?? '';

  const [serviceMode, setServiceMode] = useState<ServiceMode | null>(() => {
    const stored = getServiceModeFromLS();
    if (serviceModeInRoute && serviceModeInRoute !== stored) {
      // Sync MMKV before children can read via getServiceModeFromLS().
      // Safe under Strict Mode: serviceModeInRoute is deterministic from the URL.
      persistServiceMode(serviceModeInRoute);
    }
    return serviceModeInRoute || stored;
  });
  const enableDeliveryFeeSurchargeModal = Boolean(
    useFlag(LaunchDarklyFlag.SHOW_INCREASED_DELIVERY_FEES_MODAL)
  );

  const isDelivery = serviceMode === ServiceMode.DELIVERY;
  const isTakeout = serviceMode === ServiceMode.TAKEOUT;
  const isEatIn = serviceMode === ServiceMode.EAT_IN;
  const isMobileOrderForDriveThru = serviceMode === ServiceMode.MOBILE_ORDER_DRIVE_THRU;
  const isDriveThru = serviceMode === ServiceMode.DRIVE_THRU;

  const [
    deliverySurchargeFee,
    setDeliverySurchargeFee,
    clearDeliverySurchargeFee,
  ] = useSyncStorageNumber(SyncStorageKey.DELIVERY_SURCHARGE_FEE);

  // TODO: This is the original DeliveryFeeSurchargeDialog, we need to use this once the useDialogModal
  // renders ok within a modal screen (after GST-5015 is done)
  //
  // const [DeliveryFeeSurchargeDialog, openDeliveryFeeSurcharge] = useDialogModal({
  //   onConfirm: acceptDeliverySurchargeFee,
  //   allowDismiss: false,
  //   modalAppearanceEventMessage: 'Delivery Surcharge',
  // });

  const showDeliveryFeeSurchargeModal = enableDeliveryFeeSurchargeModal && !!deliverySurchargeFee;

  const acceptDeliverySurchargeFee = useCallback(() => {
    clearDeliverySurchargeFee();
  }, [clearDeliverySurchargeFee]);

  const selectServiceMode = useCallback((serviceMode: ServiceMode | null) => {
    toaster.hideAll();
    persistServiceMode(serviceMode);
    setServiceMode(serviceMode);

    logRBIEvent({
      name: CustomEventNames.SELECT_SERVICE_MODE,
      type: EventTypes.Other,
      attributes: null,
    });

    resetLastTimeStoreUpdated();
  }, []);

  const openDeliveryFeeSurcharge = useCallback(() => {
    // TODO: Change this native dialog with DeliveryFeeSurchargeDialog once GST-5015 is done
    Alert.alert(
      t('deliverySurcharge'),
      t('deliverySurchargeMessage', {
        feeSurcharge: currencyFormatter(deliverySurchargeFee || 0),
      }),
      [
        {
          text: t('okay'),
          onPress: () => acceptDeliverySurchargeFee(),
        },
      ]
    );
  }, [currencyFormatter, deliverySurchargeFee, acceptDeliverySurchargeFee]);

  useEffect(() => {
    if (showDeliveryFeeSurchargeModal) {
      openDeliveryFeeSurcharge();
    }
  }, [openDeliveryFeeSurcharge, showDeliveryFeeSurchargeModal]);

  useEffect(() => {
    persistServiceMode(serviceMode);
    updateUniversalAttributes({
      'Service Mode': serializeServiceMode(serviceMode),
      'Pickup Mode': serializePickupMode(serviceMode),
    });
  }, [serviceMode]);

  useEffect(() => {
    // Wait for any in-progress repricing to complete before updating service mode to avoid thread blocking.
    if (isPricingOrder) {
      return;
    }
    updateLaunchDarklyUser({ serviceMode }, 'service mode change');
  }, [serviceMode, isPricingOrder]);

  useEffect(() => {
    if (serviceModeInRoute && serviceModeInRoute !== serviceMode) {
      persistServiceMode(serviceModeInRoute);
      setServiceMode(serviceModeInRoute);
    }
  }, [serviceMode, serviceModeInRoute]);

  /**
   * Returns the service mode options for the given store.
   *
   * @param store - The store to get the service mode options for.
   * @returns The service mode options for the given store.
   */
  const getServiceModeOptions = useCallback(
    (store: IRestaurant): ServiceModeOption[] => {
      const isMobileOrderDriveThruEnabledForStore =
        mobileOrderDriveThruStores?.split(',').includes(store.number ?? '') ?? false;

      return SERVICE_MODE_ORDER.flatMap(serviceModeOption => {
        if (
          serviceModeOption === ServiceMode.DRIVE_THRU ||
          serviceModeOption === ServiceMode.MOBILE_ORDER_DRIVE_THRU
        ) {
          const preferredDriveThruServiceMode = getPreferredDriveThruServiceMode(
            isMobileOrderDriveThruEnabledForStore,
            isServiceModeAvailable,
            store
          );

          if (serviceModeOption !== preferredDriveThruServiceMode) {
            return [];
          }
        }

        const serviceModeHeadingAndIcon = getHeadingForServiceMode(
          isFastLaneEnabled,
          isMobileOrderForDriveThruLaneEnabled,
          serviceModeOption
        );

        const descriptionTextId = getServiceModeDescriptionTextId(serviceModeOption);
        const description = descriptionTextId && t(descriptionTextId);

        return {
          icon: serviceModeHeadingAndIcon?.icon ?? '',
          heading: serviceModeHeadingAndIcon?.heading ?? '',
          name: serviceModeOption,
          image: serviceModeImages[serviceModeOption],
          description: description ?? '',
          isSelected: serviceMode === serviceModeOption,
          isAvailable: isServiceModeAvailable(store, serviceModeOption),
        };
      });
    },
    [
      isFastLaneEnabled,
      isMobileOrderForDriveThruLaneEnabled,
      isServiceModeAvailable,
      mobileOrderDriveThruStores,
      serviceMode,
    ]
  );

  // Keep a mutable ref to the latest values so callbacks in the frozen
  // context can delegate to up-to-date implementations (e.g. when LD
  // flags change after the initial render).
  const latestRef = useRef({
    selectServiceMode,
    setDeliverySurchargeFee,
    getServiceModeOptions,
  });
  latestRef.current = { selectServiceMode, setDeliverySurchargeFee, getServiceModeOptions };

  // Frozen context value: object identity never changes → no subscriber
  // re-renders. Scalar fields (serviceMode, booleans) are overridden by
  // useServiceModeContext() via MMKV reads. Callbacks delegate to the
  // mutable ref so they always reflect the latest LD flags / state.
  const frozenValue = useRef<IServiceModeCtx>({
    serviceMode,
    isDelivery,
    isTakeout,
    isEatIn,
    isMobileOrderForDriveThru,
    isDriveThru,
    selectServiceMode: (...args) => latestRef.current.selectServiceMode(...args),
    setDeliverySurchargeFee: (...args) => latestRef.current.setDeliverySurchargeFee(...args),
    getServiceModeOptions: (...args) => latestRef.current.getServiceModeOptions(...args),
  }).current;

  return (
    <ServiceModeContext.Provider value={frozenValue}>
      <LiveServiceModeContext.Provider value={serviceMode}>
        {children}
      </LiveServiceModeContext.Provider>
    </ServiceModeContext.Provider>
  );
};
